/**
 * 同机时间重叠门禁（纯规则）。
 * 按飞机注册号分组，再按起飞早晚检查：后一班起飞是否早于或等于上一班到达。
 * 无注册号的航班不参与同机重叠判定；空号达到可互相打架的规模时须显式提示「无法按飞机检查」。
 * 有注册号但缺起飞/到达时刻的航班计入 skippedIncompleteCount，提示中说明未参与检查。
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/** 至少 2 班无注册号才可能在空号之间形成「看不见」的同机重叠 */
export const UNCHECKED_REGISTRATION_WARN_MIN = 2;

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toTimestamp(value) {
    if (value == null || value === "") return null;
    // 与项目惯例对齐：无时区字符串按 UTC 解析（勿用 new Date，否则会按本地时区）
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) return null;
    return parsed.valueOf();
}

/**
 * @param {unknown} reg
 * @returns {string}
 */
function normalizeRegistration(reg) {
    if (reg == null) return "";
    return String(reg).trim();
}

/**
 * @param {Array<{id?: string|number, display_order?: number}>} flights
 * @returns {Map<string, number>} flightId -> 1-based 行序（按 display_order，缺失时按传入顺序）
 */
function buildRowIndexMap(flights) {
    const ordered = [...(flights || [])].sort((a, b) => {
        const orderA = a?.display_order;
        const orderB = b?.display_order;
        if (typeof orderA === "number" && typeof orderB === "number" && orderA !== orderB) {
            return orderA - orderB;
        }
        return 0;
    });
    const rowById = new Map();
    ordered.forEach((flight, index) => {
        if (flight?.id != null) {
            rowById.set(String(flight.id), index + 1);
        }
    });
    return rowById;
}

/**
 * @param {Object} flight
 * @param {number|undefined} row
 * @returns {string}
 */
function describeFlight(flight, row) {
    const rowLabel = row != null ? `第 ${row} 行 ` : "";
    const flightNo = flight?.flight_number || "未命名航班";
    const leg = `${flight?.departure_airport || "?"}-${flight?.arrival_airport || "?"}`;
    return `${rowLabel}${flightNo}（${leg}）`;
}

/**
 * @param {string|Date|null|undefined} value
 * @param {Object|undefined} flight
 * @param {((value: *, flight?: Object, clockKind?: 'std'|'sta') => string)|undefined} formatTime
 * @param {'std'|'sta'} [clockKind='std'] - sta 用到达机场时区，std 用出发机场时区
 * @returns {string}
 */
function formatClock(value, flight, formatTime, clockKind = "std") {
    if (typeof formatTime === "function") {
        const custom = formatTime(value, flight, clockKind);
        if (custom) return custom;
    }
    const ms = toTimestamp(value);
    if (ms == null) return "?";
    // 与旧版校验文案一致：UTC 墙钟，不加「UTC」后缀，避免与表格时区标签混读
    return dayjs.utc(ms).format("YYYY-MM-DD HH:mm");
}

/**
 * @param {number} conflictCount
 * @returns {string}
 */
function formatMoreConflictsSuffix(conflictCount) {
    if (!conflictCount || conflictCount <= 1) return "";
    return `（另有 ${conflictCount - 1} 处同机冲突未展开）`;
}

/**
 * 空注册号是否已多到「无法按飞机完整检查」——不得静默放行。
 *
 * @param {{ withoutRegistrationCount?: number }} overlapResult
 * @param {number} flightCount
 * @returns {boolean}
 */
export function shouldWarnUncheckedRegistration(overlapResult, flightCount) {
    const emptyCount = overlapResult?.withoutRegistrationCount || 0;
    const total = typeof flightCount === "number" ? flightCount : 0;
    if (emptyCount < UNCHECKED_REGISTRATION_WARN_MIN) {
        return false;
    }
    if (total > 0 && emptyCount === total) {
        return true;
    }
    if (total > 0 && emptyCount / total >= 0.5) {
        return true;
    }
    return emptyCount >= UNCHECKED_REGISTRATION_WARN_MIN;
}

/**
 * @param {{ withoutRegistrationCount?: number }} overlapResult
 * @returns {string|null}
 */
export function buildUncheckedRegistrationNote(overlapResult) {
    const n = overlapResult?.withoutRegistrationCount || 0;
    if (n < UNCHECKED_REGISTRATION_WARN_MIN) {
        return null;
    }
    return `有 ${n} 班未填飞机注册号，无法按飞机完整检查同机时间冲突。请补全注册号后再次校验，或改用行内「重算」并人工核对时刻。`;
}

/**
 * @param {{ skippedIncompleteCount?: number }} overlapResult
 * @returns {string|null}
 */
export function buildSkippedIncompleteNote(overlapResult) {
    const n = overlapResult?.skippedIncompleteCount || 0;
    if (n <= 0) return null;
    return `另有 ${n} 班有注册号但缺少起飞或到达时刻，未参与同机重叠检查，请补全时刻后再次校验。`;
}

/**
 * 把空号 / 缺时刻补充说明拼到主提示后。
 *
 * @param {string} message
 * @param {ReturnType<typeof findSameAircraftTimeOverlaps>} overlapResult
 * @returns {string}
 */
function appendCoverageNotes(message, overlapResult) {
    let next = message;
    const uncheckedNote = buildUncheckedRegistrationNote(overlapResult);
    if (uncheckedNote) {
        next = `${next} ${uncheckedNote}`;
    }
    const incompleteNote = buildSkippedIncompleteNote(overlapResult);
    if (incompleteNote) {
        next = `${next} ${incompleteNote}`;
    }
    return next;
}

/**
 * 在整份草稿上查找同机时间重叠。
 *
 * @param {Array} flights - 试算后拟呈现的完整航班列表（含段外旧时刻）
 * @param {{ orderedFlights?: Array }} [options] - 可选；指定用于行序展示的有序列表
 * @returns {{
 *   hasOverlap: boolean,
 *   conflicts: Array<{
 *     aircraftReg: string,
 *     previousFlight: Object,
 *     overlappingFlight: Object,
 *     previousRow: number|undefined,
 *     overlappingRow: number|undefined,
 *     previousSta: *,
 *     overlappingStd: *,
 *   }>,
 *   withoutRegistrationCount: number,
 *   skippedIncompleteCount: number,
 * }}
 */
export function findSameAircraftTimeOverlaps(flights, options = {}) {
    const list = Array.isArray(flights) ? flights : [];
    const rowSource = Array.isArray(options.orderedFlights) && options.orderedFlights.length > 0 ? options.orderedFlights : list;
    const rowById = buildRowIndexMap(rowSource);

    const byReg = new Map();
    let withoutRegistrationCount = 0;
    let skippedIncompleteCount = 0;

    list.forEach((flight) => {
        const reg = normalizeRegistration(flight?.aircraft_reg);
        if (!reg) {
            withoutRegistrationCount += 1;
            return;
        }
        const stdMs = toTimestamp(flight?.std);
        const staMs = toTimestamp(flight?.sta);
        if (stdMs == null || staMs == null) {
            skippedIncompleteCount += 1;
            return;
        }
        if (!byReg.has(reg)) {
            byReg.set(reg, []);
        }
        byReg.get(reg).push({ flight, stdMs, staMs });
    });

    const conflicts = [];

    byReg.forEach((group, aircraftReg) => {
        const sortable = [...group].sort((a, b) => {
            if (a.stdMs !== b.stdMs) return a.stdMs - b.stdMs;
            const orderA = a.flight?.display_order;
            const orderB = b.flight?.display_order;
            if (typeof orderA === "number" && typeof orderB === "number" && orderA !== orderB) {
                return orderA - orderB;
            }
            return String(a.flight?.id || "").localeCompare(String(b.flight?.id || ""));
        });

        for (let i = 1; i < sortable.length; i += 1) {
            const previous = sortable[i - 1];
            const current = sortable[i];
            // 起飞早于或等于上一班到达 → 同机时间重叠
            if (current.stdMs <= previous.staMs) {
                const previousFlight = previous.flight;
                const overlappingFlight = current.flight;
                conflicts.push({
                    aircraftReg,
                    previousFlight,
                    overlappingFlight,
                    previousRow: rowById.get(String(previousFlight.id)),
                    overlappingRow: rowById.get(String(overlappingFlight.id)),
                    previousSta: previousFlight.sta,
                    overlappingStd: overlappingFlight.std,
                });
            }
        }
    });

    return {
        hasOverlap: conflicts.length > 0,
        conflicts,
        withoutRegistrationCount,
        skippedIncompleteCount,
    };
}

/**
 * 「重算选中」门禁提示：点名冲突班对，并给出扩大勾选 / 行内重算出路。
 *
 * @param {ReturnType<typeof findSameAircraftTimeOverlaps>} overlapResult
 * @param {{ selectedIds?: Array<string|number>, formatTime?: (value: *, flight?: Object, clockKind?: 'std'|'sta') => string }} [options]
 * @returns {string|null}
 */
export function buildRecalcOverlapGateMessage(overlapResult, options = {}) {
    if (!overlapResult?.hasOverlap || !overlapResult.conflicts?.length) {
        return null;
    }

    const formatTime = options.formatTime;
    const conflict = overlapResult.conflicts[0];
    const prevDesc = describeFlight(conflict.previousFlight, conflict.previousRow);
    const nextDesc = describeFlight(conflict.overlappingFlight, conflict.overlappingRow);
    const more = formatMoreConflictsSuffix(overlapResult.conflicts.length);
    const pairDetail = `${prevDesc} 到达 ${formatClock(conflict.previousSta, conflict.previousFlight, formatTime, "sta")}，但 ${nextDesc} 起飞 ${formatClock(conflict.overlappingStd, conflict.overlappingFlight, formatTime, "std")}（不晚于前班到达）${more}`;

    const selectedSet = new Set((options.selectedIds || []).map((id) => String(id)));
    const bothInSelection =
        selectedSet.size > 0 &&
        selectedSet.has(String(conflict.previousFlight?.id)) &&
        selectedSet.has(String(conflict.overlappingFlight?.id));

    let message;
    if (bothInSelection) {
        message = `同一架飞机时间冲突：${pairDetail}。冲突发生在选中段内部，请检查计算配置或段内时刻后再重试。`;
    } else {
        // 中性措辞：冲突班可能在选中段前或后，避免只说「后续」
        message = `同一架飞机时间冲突：${pairDetail}。请把冲突涉及的另一班一并勾进连续一段（可向前或向后扩大勾选）后再点「重算选中」，或从更早的班使用行内「重算」。`;
    }

    return appendCoverageNotes(message, overlapResult);
}

/**
 * 保存前硬拦提示。
 *
 * @param {ReturnType<typeof findSameAircraftTimeOverlaps>} overlapResult
 * @param {{ formatTime?: (value: *, flight?: Object, clockKind?: 'std'|'sta') => string }} [options]
 * @returns {string|null}
 */
export function buildSaveOverlapGateMessage(overlapResult, options = {}) {
    if (!overlapResult?.hasOverlap || !overlapResult.conflicts?.length) {
        return null;
    }

    const formatTime = options.formatTime;
    const conflict = overlapResult.conflicts[0];
    const prevDesc = describeFlight(conflict.previousFlight, conflict.previousRow);
    const nextDesc = describeFlight(conflict.overlappingFlight, conflict.overlappingRow);
    const more = formatMoreConflictsSuffix(overlapResult.conflicts.length);

    const message = `存在同机时间冲突，无法保存：${prevDesc} 到达 ${formatClock(conflict.previousSta, conflict.previousFlight, formatTime, "sta")}，但 ${nextDesc} 起飞 ${formatClock(conflict.overlappingStd, conflict.overlappingFlight, formatTime, "std")}（不晚于前班到达）${more}。请先调整时刻或扩大重算范围后再保存。`;

    return appendCoverageNotes(message, overlapResult);
}

const SameAircraftOverlapService = {
    findSameAircraftTimeOverlaps,
    buildRecalcOverlapGateMessage,
    buildSaveOverlapGateMessage,
    shouldWarnUncheckedRegistration,
    buildUncheckedRegistrationNote,
    buildSkippedIncompleteNote,
    UNCHECKED_REGISTRATION_WARN_MIN,
};

export default SameAircraftOverlapService;
